import {
  APPWRITE_DATABASE_ID,
  PROJECT_PAGES_TABLE_ID,
  client,
  tablesDB
} from "src/handlers/Appwrite";
import { ensureSharedPageRow } from "src/handlers/ProjectSync";
import { logActivity } from "src/handlers/Activity";
import { beginSave } from "src/handlers/syncStatus";

import { classifyWriteError } from "./fallback";
import {
  buildPageRowData,
  getProjectId,
  makePageRowId,
  parseJson
} from "./internal";

// ---------------------------------------------------------------------------
// Page rows model the shared, non-device project state (currently the VLAN
// list). One row per (projectId, pageKey).
// ---------------------------------------------------------------------------

async function getPageRow(page) {
  const projectId = getProjectId();
  if (!projectId) return null;

  try {
    return await tablesDB.getRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PAGES_TABLE_ID,
      rowId: makePageRowId(projectId, page)
    });
  } catch (error) {
    if (error?.code === 404) return null;
    throw error;
  }
}

/**
 * Persist a project-scoped page row (currently only `vlan`).
 * @param {string} page
 * @param {any} data
 * @returns {Promise<import("src/handlers/types").PageRow | undefined>}
 */
export async function syncPageData(page, data) {
  const projectId = getProjectId();
  if (!projectId) return;
  const rowId = makePageRowId(projectId, page);
  const rowData = buildPageRowData(projectId, page, data);

  // Report progress to the global save-status store so the chip in the
  // header reflects the outcome.
  const end = beginSave();
  try {
    const result = await tablesDB.updateRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PAGES_TABLE_ID,
      rowId,
      data: rowData
    });
    // Phase H2: fire-and-forget audit. logActivity never throws.
    logActivity({ kind: "page", payload: { pageKey: page } });
    end();
    return result;
  } catch (error) {
    const decision = classifyWriteError({
      context: "pages.upsert",
      code: error?.code,
      error,
      page
    });
    if (decision.action === "throw") {
      end(decision.error);
      throw decision.error;
    }
  }

  try {
    const result = await ensureSharedPageRow({
      projectId,
      pageKey: page,
      data
    });
    logActivity({ kind: "page", payload: { pageKey: page } });
    end();
    return result;
  } catch (error) {
    end(error);
    throw error;
  }
}

export async function syncPageDown(page) {
  const row = await getPageRow(page);
  const data = parseJson(row?.dataJson, null);
  if (data !== null) {
    localStorage.setItem(`${page}_data`, JSON.stringify(data));
  }
  return data;
}

export function subscribePageSync(page, callback) {
  const projectId = getProjectId();
  if (!projectId) return () => {};

  return client.subscribe(
    `databases.${APPWRITE_DATABASE_ID}.tables.${PROJECT_PAGES_TABLE_ID}.rows`,
    (event) => {
      const row = event?.payload;
      if (!row) return;
      if (row.projectId !== projectId) return;
      if (row.pageKey !== page) return;

      callback?.(parseJson(row.dataJson, null));
    }
  );
}

export async function deletePageRow(page) {
  const projectId = getProjectId();
  if (!projectId) return;

  try {
    await tablesDB.deleteRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PAGES_TABLE_ID,
      rowId: makePageRowId(projectId, page)
    });
  } catch (error) {
    if (error?.code !== 404) throw error;
  }
}
